"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { RootState } from "@/store/store";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LEAD_DROP_FORM_SECTION_VARIANTS,
  LEAD_DROP_FORM_VARIANTS,
  LEAD_DROP_VARIANTS,
} from "../../../constants";

/**
 * About 1037 — BDU "About <programme>".
 *
 * Port of BDU v1 `components/Degree/About.tsx`: a left column with the heading, a
 * gradient rule, justified body copy and a two-up grid of dotted keypoint chips —
 * and, from 768px up, the site's inline lead form on the right. Below 768px the form
 * is replaced by a single full-width "Enroll Now" button that opens the lead-drop
 * popup, exactly as in the reference.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here (this app remaps `md:` to 1230px).
 *
 * LEAD FORM — the reference hardcodes its own `PageLeadDropForm`. This app resolves
 * the form from the tenant's configured Lead Drop Form variant (same wiring as About
 * 2 / Banner 10), so the programme page picks up whichever form the site uses.
 *
 * SSR — the copy (heading, body, keypoints) and the mobile CTA are server-rendered;
 * CSS alone decides which breakpoint shows the CTA. Only the inline form waits for
 * `useIsMobile` to resolve to `false`: its variant lives in client-populated state,
 * and mounting it behind `display:none` on phones would run its effects for a form
 * nobody sees. Do NOT go back to returning null until `isMobile` resolves — that hid
 * the whole section from the server HTML (and from crawlers reading it).
 *
 * MOBILE — unlike About 2 / Banner 10 this does NOT fall back to the form variant's
 * mapped mobile CTA. Several of those (form 5002, form 9) render the whole inline
 * form rather than a button, and v1 shows a plain CTA here at every mobile width.
 */

interface KeypointRow {
  id?: string | number;
  meta_icon?: string;
  meta_icon_alt?: string;
  meta_title?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface About1037Data {
  metadata?: KeypointRow[];
  background_color?: string;
  title?: string;
  title_text_color?: string;
  description?: string;
  description_text_color?: string;
  /** Rule under the heading */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Keypoint chip accent bar (desktop only, as in the reference) */
  gradient_color_3?: string;
  gradient_color_4?: string;
  /** Keypoint chip dotted border */
  highlight_border_color?: string;
  enable_lead_form?: boolean;
  /** Label for the full-width CTA shown below 768px in place of the form */
  mobile_apply_now_text?: string;
  status?: boolean;
}

const About1037 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as About1037Data | undefined;
  const dispatch = useDispatch();
  const isMobile = useIsMobile(768);

  const leaddropFormVariant = useSelector(
    (state: RootState) => state.leaddropform.variant,
  );
  const leadDropFormRow = useSelector((state: RootState) => {
    const blob = state.leaddropform.lead_drop_form as
      | { formData?: Record<string, unknown> }
      | null
      | undefined;
    return blob?.formData ?? null;
  });

  const LeaddropFormComponent = useMemo(() => {
    if (!leaddropFormVariant) return null;
    return LEAD_DROP_FORM_SECTION_VARIANTS[leaddropFormVariant] ?? null;
  }, [leaddropFormVariant]);

  const keypoints = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) =>
          (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  const {
    background_color = "#FFFFFF",
    title = "",
    title_text_color = "#222023",
    description = "",
    description_text_color = "#222023",
    gradient_color_1 = "#1F3D56",
    gradient_color_2 = "#7CB5E6",
    gradient_color_3 = "#FD0113",
    gradient_color_4 = "#9E0113",
    highlight_border_color = "#EF4444",
    mobile_apply_now_text = "Enroll Now",
  } = sectionData || {};

  const enableLeadForm = isCmsShowFlagOn(sectionData?.enable_lead_form, true);

  const handleEnrollClick = useCallback(() => {
    ctaClick({
      section: "about_1037",
      component: "enroll_now",
      label: mobile_apply_now_text?.trim() || "enroll_now",
      step: "1",
      entity: "about_1037",
      parentEntity: "about_1037",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [dispatch, mobile_apply_now_text]);

  if (!sectionData?.status) return null;

  // Rows seeded before the background field existed carry a NULL column, which the
  // destructuring default above does not cover — fall back to white for those too.
  return (
    <div
      className="flex w-full flex-col justify-between gap-5 px-[18px] py-6 tab:flex-row tab:items-center tab:gap-10 tab:px-16 tab:py-9"
      style={{ backgroundColor: background_color || "#FFFFFF" }}
    >
      <div className="flex flex-1 flex-col gap-[18px]">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <h2
              className="text-2xl font-bold tab:text-[28px]"
              style={{ color: title_text_color }}
            >
              {title}
            </h2>
            <div
              className="h-[3px] w-[226px]"
              style={{
                backgroundImage: `linear-gradient(to right, ${gradient_color_1}, ${gradient_color_2})`,
              }}
            />
          </div>
          <div
            className="whitespace-pre-wrap text-justify text-xs/7 leading-7 tab:text-sm/7"
            style={{ color: description_text_color }}
          >
            {description}
          </div>
        </div>

        {keypoints.length > 0 && (
          <div className="grid grid-cols-2 gap-1 tab:gap-x-4 tab:gap-y-[18px]">
            {keypoints.map((item, index) => (
              <div
                key={item.id ?? index}
                className="relative rounded-md border border-dotted px-4 py-2.5"
                style={{ borderColor: highlight_border_color }}
              >
                <div
                  className="absolute left-[-1px] top-1.5 hidden h-[calc(100%-12px)] w-[3px] rounded tab:block"
                  style={{
                    backgroundImage: `linear-gradient(to bottom, ${gradient_color_3}, ${gradient_color_4})`,
                  }}
                />
                <div className="flex flex-col gap-0 tab:flex-row tab:items-center tab:gap-2.5">
                  {item.meta_icon && (
                    <Image
                      src={item.meta_icon}
                      alt={item.meta_icon_alt || item.meta_title || ""}
                      width={32}
                      height={32}
                      loading="lazy"
                      className="size-6 shrink-0 tab:size-8"
                    />
                  )}
                  <p
                    className="text-xs font-semibold tab:text-sm"
                    style={{ color: description_text_color }}
                  >
                    {item.meta_title}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Desktop — inline lead form. `=== false`, not `!isMobile`: null (server and
          first client render) must render nothing here so hydration matches. */}
      {enableLeadForm && isMobile === false && LeaddropFormComponent && (
        <div className={cn("hidden overflow-hidden", "tab:block tab:shrink-0")}>
          {leaddropFormVariant === LEAD_DROP_FORM_VARIANTS.LEAD_DROP_FORM_6 &&
          leadDropFormRow ? (
            <LeaddropFormComponent data={{ data: leadDropFormRow }} />
          ) : (
            <LeaddropFormComponent />
          )}
        </div>
      )}

      {/* Below 768px — the reference's plain Enroll Now button, which opens the popup.
          Always in the server HTML; `tab:hidden` removes it from 768px up. The wrapper
          is a plain block (the button is w-full) so it carries no second `display`
          utility to collide with `tab:hidden`. */}
      {enableLeadForm && (
        <div className="w-full tab:hidden">
          <button
            type="button"
            onClick={handleEnrollClick}
            className="h-10 w-full rounded-md text-base font-medium leading-6 text-white shadow"
            style={{
              backgroundImage: `linear-gradient(to right, ${gradient_color_3}, ${gradient_color_4})`,
            }}
          >
            {mobile_apply_now_text}
          </button>
        </div>
      )}
    </div>
  );
};

export default About1037;
